import {FormFieldType} from "@app/components/forms/types/schema"
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import {
  CommonModal,
  CommonSelectModal,
} from "@app/components/modals/CommonModal";
import { PostingGroupChangeModal } from "@app/components/modals/PostingSelectorModal";
import { moreMenuItems } from "@app/components/PostingList/PostingCard";
import {
  InspectorTabs,
  InspectorTabItem,
} from "@app/components/WidgetStudio/Inspector/contracts/inspectorTabs";
import { TabIds } from "@app/pages/Postings";
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuItem,
  DropdownMenuContent,
  useModal,
  TooltipProvider,
  ToggleGroup,
  Separator,
  Tooltip,
  TooltipTrigger,
  ToggleGroupItem,
  TooltipContent,
} from "@shortsworks/ui";
import {
  useUpdateStoryMetadata,
  useUpdateStoriesMetadata,
  useGetStories,
  useDeleteStories, useGetStoryGroupList,
} from "@shortsworks/user-api"
import { useQueryClient } from "@tanstack/react-query";
import { Pin, Trash, Move, Group, ArrowLeftRight } from "lucide-react";
import { useEffect, useState, Children, Dispatch, SetStateAction } from "react";

const targetMenuItemTypes = ["delete", "change_group", "set_as_notice"];
type TargetType = (typeof targetMenuItemTypes)[number];
const targetMenuItems = moreMenuItems.filter((item) =>
  targetMenuItemTypes.includes(item.type),
);

const dropDownItemTypes = [
  "move_to_draft",
  "move_to_published",
  "move_to_hidden",
];

interface PostingToolBarProps {
  selectedTab: TabIds;
  selectedIds: Map<string, string[]> | undefined;
  onSelectedIds: Dispatch<SetStateAction<Map<TabIds, string[]> | undefined>>;
}

export const PostingToolbar = ({
  selectedTab,
  selectedIds,
  onSelectedIds,
}: PostingToolBarProps) => {
  const { showModal } = useModal();
  const isEmptySelectedIds = !(selectedIds && [...selectedIds.values()].flat().length !== 0);

  const queryClient = useQueryClient();

  const {
    data: storiesData,
    queryKey: storiesQueryKey,
    isSuccess: isSuccessGetStories,
  } = useGetStories();

  const {data: storyGroupListData, isSuccess: isSuccessGetStoryGroupList} = useGetStoryGroupList();

  const { mutateAsync: mutateAsyncUpdateStoryMetaData } =
    useUpdateStoriesMetadata({
      mutation: {
        onSuccess: () => {
          return queryClient.invalidateQueries({ queryKey: storiesQueryKey });
        },
        onError: () => {
          return showModal(CommonErrorModal, { title: "변경을 실패했습니다." });
        },
      },
    });

  const { mutateAsync: mutateAsyncDeleteStories } = useDeleteStories({
    mutation: {
      onSuccess: () => {
        onSelectedIds(undefined);
        return queryClient.invalidateQueries({ queryKey: storiesQueryKey });
      },
    },
  });

  const handleValueChange = (itemType: TargetType) => async () => {
    if (!selectedIds) {
      return showModal(CommonErrorModal, {
        title: "선택된 포스팅이 없습니다.",
      });
    }

    if (itemType === "set_as_notice") {
      const modalResult = await showModal(CommonSelectModal, {title: "선택된 포스팅들을 공지하시겠습니까?"}).promise();
      if(modalResult.value) {
        return mutateAsyncUpdateStoryMetaData({
          data: {
            storyIds: [...selectedIds?.values()].flat(),
            pin: true
          }
        });
      }
    }

    if (itemType === "change_group") {
      if(!isSuccessGetStoryGroupList) {
        return showModal(CommonErrorModal, {title: "그룹 리스트를 불러오는데 실패했습니다. 다시 시도해주세요."});
      }
      return showModal(PostingGroupChangeModal, {
        storyIds: [...selectedIds?.values()].flat(),
        groupIds: [],
        groupListSchema:
            storyGroupListData.data.map((group) => {
              return {
                id: group.id,
                label: group.name,
                type: FormFieldType.Switch,
              };
            }) ?? [],
      });
    }

    if (itemType === "delete") {
      const modalResult = await showModal(CommonSelectModal, {
        title: "선택한 포스팅들을 삭제하시겠습니까?",
      }).promise();
      if (!modalResult.value) {
        return;
      }
      return mutateAsyncDeleteStories({
        params: {
          storyIds: [...selectedIds.values()].flat(),
        },
      });
    }
  };

  // toggle group이 실제로 사용되지 않고 있어 ui만 받도록 수정 필요
  return (
    <TooltipProvider>
      <ToggleGroup
        type="single"
        size="sm"
        variant="outline"
      >
        {Children.toArray(
          targetMenuItems.map((item) => (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* 디자인만 가져오로*/}
                <ToggleGroupItem disabled={isEmptySelectedIds} onClick={handleValueChange(item.type)} value={item.type}>
                  {item.icon && <item.icon className="h-4 w-4" />}
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>{item.title}</TooltipContent>
            </Tooltip>
          )),
        )}
      </ToggleGroup>
    </TooltipProvider>
  );
};
